import React, { useContext, useEffect, useState } from "react";
import classes from "./ResidentsPage.module.css";
import { ResidentData, ResidentDataCreateResponse, ResidentShortData } from "../../types/api/resident";
import { createNewResident, deleteResident, editResident, getResident, getResidents } from "../../api/residentAPI";
import Loader from "../../components/Loader/Loader";
import { useParams, useSearchParams } from "react-router-dom";
import Modal from "../../components/UI/Modal/Modal";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { MainContext, MainContextState } from "../../context/MainContext";
import {
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Pagination,
    Paper,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from "@mui/material";
import { useNotification } from "../../hooks/useNotification";
import { MODAL_TYPE } from "../../constants/common";
import Search from "../../components/Search/Search";
import CustomSnackbar from "../../components/UI/CustomSnackbar";
import { getEquipments } from "../../api/equipmentAPI";
import { EquipmentData } from "../../types/api/equipment";
import { DocumentData } from "../../types/api/documents";
import { ResidentForm } from "../../components/forms/ResidentForm/ResidentForm";
import { getDateFormat } from "../../utils/getDateFormat";

const ResidentsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const queryResidentId = searchParams.get("residentId");

    const isOwner =
        localStorage.getItem("employee_type") == "16" &&
        localStorage.getItem("employee_center_id") == localStorage.getItem("current_center_id");
    const { constants } = useContext<Partial<MainContextState>>(MainContext);
    const tableHeaders = ["ФИО", "Дата \n" + "рождения", "Телефон", "email", "Статус", ""];
    const { id } = useParams();
    const [load, setLoad] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
    });
    const [residents, setResidents] = useState<Array<ResidentData>>();
    const [viewDocuments, setViewDocuments] = useState<Array<DocumentData>>();
    const [equipments, setEquipments] = useState<Array<EquipmentData>>();
    const [residentId, setResidentId] = useState(0);
    const [deleteResidentId, setDeleteResidentId] = useState(0);
    const [query, setQuery] = useState("");
    const { setNotification, setOpenSnackbar, notification, openSnackbar } = useNotification();

    const [showModal, setShowModal] = useState<boolean>(false);
    const [currentModalType, setCurrentModalType] = useState("edit");
    const closeModal = () => {
        setShowModal(false);
    };
    const [type, setType] = useState("1");

    const fetchResidents = (query?: string) => {
        setLoad(true);
        getResidents(
            id,
            ({ data, last_page, current_page }) => {
                setResidents(data);
                setPagination({ ...pagination, last_page: last_page, current_page: current_page });
                setLoad(false);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setLoad(false);
            },
            query
                ? { page: pagination.current_page, name: query, type: type }
                : { page: pagination.current_page, type: type },
        );
    };

    const editResetFormData = (data: ResidentData) => {
        editForm.reset({
            ...data,
            birthday: data.birthday?.slice(0, 10).replace(/-/g, "/"),
            contracts: data.contracts?.map((contract: any) => {
                return {
                    ...contract,
                    sum: Number.isInteger(contract.sum) ? contract.sum / 100 : contract.sum,
                    from: contract.from?.slice(0, 10).replace(/-/g, "/"),
                    to: contract.to?.slice(0, 10).replace(/-/g, "/"),
                };
            }),
            documents: [],
        });
        setViewDocuments(data.documents);
    };

    const getResidentData = (residentId: number) => {
        setLoad(true);
        getResident(
            id,
            residentId,
            ({ data }) => {
                editResetFormData(data);
                setCurrentModalType(MODAL_TYPE.edit);
                setLoad(false);
                if (queryResidentId) {
                    setTimeout(() => {
                        setShowModal(true);
                        setSearchParams({});
                    }, 100);
                } else {
                    setShowModal(true);
                }
            },
            err => {
                setLoad(false);
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
            },
        );
    };

    const createForm = useForm<ResidentShortData>();
    const editForm = useForm<ResidentDataCreateResponse>();

    const create: SubmitHandler<ResidentShortData> = (data: any) => {
        setLoad(true);
        if (data.type === 512) {
            delete data.requisites.passport_seria;
            delete data.requisites.passport_number;
            delete data.requisites.passport_place;
            delete data.requisites.passport_place_code;
            delete data.requisites.passport_date;
            delete data.requisites.snils;
        } else {
            delete data.requisites.account;
            delete data.requisites.bik;
            delete data.requisites.kpp;
            delete data.address;
        }
        createNewResident(
            id,
            { ...data, contracts: data.contracts.map((el: any) => ({ ...el, sum: el.sum * 100 })) },
            () => {
                fetchResidents(query);
                setNotification({
                    severity: "success",
                    message: "Резидент добавлен успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
                closeModal();
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setOpenSnackbar(true);
                setLoad(false);
                err?.errors &&
                    Object.keys(err?.errors).forEach((fieldName: any) => {
                        createForm.setError(fieldName, {
                            type: "custom",
                            message: err?.errors[fieldName][0],
                        });
                    });
            },
        );
    };

    const edit: SubmitHandler<ResidentShortData> = (data: any) => {
        setLoad(true);
        if (data.type === 512) {
            delete data.requisites.passport_seria;
            delete data.requisites.passport_number;
            delete data.requisites.passport_place;
            delete data.requisites.passport_place_code;
            delete data.requisites.passport_date;
            delete data.requisites.snils;
        } else {
            delete data.requisites.account;
            delete data.requisites.bik;
            delete data.requisites.kpp;
            delete data.address;
        }
        editResident(
            id,
            residentId,
            { ...data, contracts: data.contracts.map((el: any) => ({ ...el, sum: el.sum * 100 })) },

            ({ data }) => {
                editResetFormData(data);
                fetchResidents(query);
                setNotification({
                    severity: "success",
                    message: "Данные резидента изменены успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
                closeModal();
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setLoad(false);
                setOpenSnackbar(true);
                Object.keys(err.errors).forEach((fieldName: any) => {
                    editForm.setError(fieldName, {
                        type: "custom",
                        message: err.errors[fieldName][0],
                    });
                });
            },
        );
    };
    const del: SubmitHandler<any> = () => {
        setLoad(true);
        deleteResident(
            id,
            deleteResidentId,
            () => {
                fetchResidents(query);
                setNotification({
                    severity: "success",
                    message: "Резидент удален успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setOpenSnackbar(true);
                setLoad(false);
            },
        );
        closeModal();
    };

    const createContent = (
        <FormProvider {...createForm}>
            <ResidentForm equipments={equipments} />
        </FormProvider>
    );
    const editContent = (
        <FormProvider {...editForm}>
            <ResidentForm
                equipments={equipments}
                editForm
                viewDocuments={viewDocuments}
                setViewDocuments={setViewDocuments}
            />
        </FormProvider>
    );
    const deleteContent = <div>Вы уверены, что хотите удалить данные о резиденте?</div>;

    const modalType = {
        [MODAL_TYPE.create]: {
            title: "Создание резидента",
            onSubmit: createForm.handleSubmit(create),
            content: createContent,
        },
        [MODAL_TYPE.edit]: {
            title: "Редактирование резидента",
            onSubmit: editForm.handleSubmit(edit),
            content: editContent,
        },
        [MODAL_TYPE.delete]: {
            title: "Удаление резидента",
            onSubmit: del,
            content: deleteContent,
        },
    };

    useEffect(() => {
        setLoad(true);
        fetchResidents(query);
    }, [pagination.current_page, type, query]);

    useEffect(() => {
        if (residentId) {
            getResidentData(residentId);
        }
    }, [residentId]);

    useEffect(() => {
        if (queryResidentId) {
            setShowModal(false);
            setResidentId(Number(queryResidentId));
        }
    }, [queryResidentId]);

    useEffect(() => {
        getEquipments(
            id,
            ({ data }) => {
                setEquipments(data);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
            },
        );
    }, []);

    return (
        <div className={"container"}>
            <div className={"pageHeader"}>Резиденты</div>
            <div className={classes.residentPage__buttons}>
                <FormControl
                    sx={{
                        marginTop: "16px",
                        marginBottom: "8px",
                        width: "360px",
                    }}>
                    <InputLabel id={"resident-type-label"}>Тип резидента</InputLabel>
                    <Select
                        size={"small"}
                        labelId={"resident-type"}
                        label={"Тип резидента"}
                        onChange={(event: SelectChangeEvent) => {
                            setType(event.target.value);
                        }}
                        value={type}>
                        {constants?.resident_type.map(type => (
                            <MenuItem key={type.value} value={type.value}>
                                {type.text}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Search
                    clearHandler={value => {
                        setQuery(value);
                    }}
                    searchHandler={value => {
                        setQuery(value);
                    }}
                />
            </div>
            <TableContainer component={Paper} sx={{ marginBottom: "80px" }}>
                <Table aria-label="centers table" sx={{ "& td": { border: "none" } }}>
                    <TableHead sx={{ "& th": { fontSize: "16px", fontWeight: 700 } }}>
                        <TableRow>
                            {tableHeaders.map((header, index) => (
                                <TableCell key={index} align={index !== 0 ? "center" : "left"}>
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {residents?.map(resident => (
                            <TableRow key={resident.id} sx={{ border: 0 }}>
                                <TableCell sx={{ wordWrap: "break-word" }}>{resident.name}</TableCell>
                                <TableCell align="center">{getDateFormat(resident.birthday)}</TableCell>
                                <TableCell align="center">{resident.phone}</TableCell>
                                <TableCell align="center">{resident.email}</TableCell>
                                <TableCell align="center">
                                    <Tooltip title={resident.status.message !== "null" && resident.status.message}>
                                        <div>
                                            {
                                                constants?.resident_state.find(
                                                    state => state.value === Number(resident.status.state),
                                                )?.text
                                            }
                                            <br />
                                            {getDateFormat(resident.status.created_at)}
                                        </div>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="center">
                                    {isOwner && (
                                        <div className={"btnContainer"}>
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                onClick={() => {
                                                    residentId === resident.id && setShowModal(true);
                                                    setResidentId(resident.id);
                                                    setCurrentModalType(MODAL_TYPE.edit);
                                                }}>
                                                Изменить
                                            </Button>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                onClick={() => {
                                                    setShowModal(true);
                                                    setDeleteResidentId(resident.id);
                                                    setCurrentModalType(MODAL_TYPE.delete);
                                                }}>
                                                Удалить
                                            </Button>
                                        </div>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <div className={"posBR"}>
                <div className={"paginationContainer"}>
                    <Pagination
                        onChange={(index, qwerty) => setPagination({ ...pagination, current_page: qwerty })}
                        page={pagination.current_page}
                        count={pagination.last_page}
                        shape="rounded"
                    />
                </div>
                {isOwner && (
                    <Button
                        variant="outlined"
                        onClick={() => {
                            createForm.reset();
                            setCurrentModalType(MODAL_TYPE.create);
                            setShowModal(true);
                        }}>
                        Добавить
                    </Button>
                )}
            </div>
            <Modal
                open={showModal}
                title={modalType[currentModalType]?.title}
                onClose={() => {
                    closeModal();
                }}
                onSubmit={modalType[currentModalType]?.onSubmit}>
                {modalType[currentModalType]?.content}
            </Modal>

            <CustomSnackbar openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} notification={notification} />
            <Loader backDrop open={load} />
        </div>
    );
};

export default ResidentsPage;
